import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  DataGrid,
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
  GridRowSelectionModel,
  GridSortModel,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import ArticleIcon from "@mui/icons-material/Article";
import TableFilters from "./TableFilters";
import StatementsDrawer from "../statementsDrawer/StatementsDrawer";
import { getTransactionsById } from "../../services/transactionService";
import { setSelectedRecTransaction } from "../../redux/features/reconcileSlice";
import { TransactionDetailsType } from "../../types/genericType";
import "./styles.scss";
import { Typography } from "@mui/material";
import { convertToLocalWithoutUTC } from "../../utils/dateFormat";
import { formatDollar } from "../../utils/utils";

type StatementTableProps = {
  data: Array<TransactionDetailsType>;
  search: string;
  status: string;
  rowSelectionModel: GridRowSelectionModel | undefined;
  setStatus: Function;
  clearSearch: Function;
  setRowSelectionModel: Function;
  // eslint-disable-next-line no-unused-vars
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const StatementTable = (props: StatementTableProps) => {
  const {
    data,
    search,
    status,
    rowSelectionModel,
    onSearchChange,
    clearSearch,
    setStatus,
    setRowSelectionModel,
  } = props;
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "date", sort: "desc" },
  ]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };
  const [open, setOpen] = React.useState(false);
  const dispatch = useDispatch();

  const onViewStatement = (transaction: TransactionDetailsType) => {
    getTransactionsById(transaction.id).then((res) => {
      dispatch(setSelectedRecTransaction({ ...transaction, ...res }));
      setOpen(true);
    });
  };

  const columns: GridColDef[] = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "date",
        headerName: "Date",
        minWidth: 150,
        headerClassName: "header-datagrid",
        valueFormatter: (params: GridValueFormatterParams) =>
          convertToLocalWithoutUTC(params.value).format("MM/DD/YYYY"),
      },
      {
        field: "client",
        headerName: "Client",
        minWidth: 200,
        sortable: false,
        headerClassName: "header-datagrid",
        clickable: true,
      },
      {
        field: "account",
        headerName: "Account #",
        sortable: false,
        headerClassName: "header-datagrid",
        cellClassName: "client-cell",
      },
      {
        field: "accountName",
        headerName: "Account",
        minWidth: 150,
        sortable: false,
        headerClassName: "header-datagrid",
      },
      {
        field: "checkNumber",
        headerName: "Check/ACH/Wire",
        minWidth: 150,
        sortable: false,
        headerClassName: "header-datagrid",
        valueGetter: (params: GridValueGetterParams) => {
          if (params.row.checkNumber) {
            return params.row.checkNumber;
          } else if (params.row.wireNumber) {
            return params.row.wireNumber;
          } else if (params.row.achDetails) {
            return params.row.achDetails;
          }
          return "";
        },
      },
      {
        field: "code",
        headerName: "Code",
        sortable: false,
        headerClassName: "header-datagrid",
      },
      {
        field: "status",
        headerName: "Status",
        sortable: false,
        headerClassName: "header-datagrid",
        valueFormatter: (params: GridValueFormatterParams) =>
          params.value.toString().substring(0, 1),
      },
      {
        field: "description",
        headerName: "Description",
        minWidth: 300,
        sortable: false,
        headerClassName: "header-datagrid",
      },
      {
        field: "amount",
        headerName: "Amount",
        sortable: false,
        sortComparator: (a: string, b: string) =>
          Number(a.slice(1)) - Number(b.slice(1)),
        valueGetter: (params: GridValueGetterParams) =>
          `${formatDollar(params.row.amount)}`,
        headerClassName: "header-datagrid",
        cellClassName: (params: GridCellParams<any, any>) =>
          params.row.amount >= 0 ? "positive-cell" : "negative-cell",
      },
      {
        field: "balance",
        headerName: "Balance",
        sortable: false,
        minWidth:140,
        headerClassName: "header-datagrid",
        valueGetter: (params: GridValueGetterParams) =>
          `$ ${formatDollar(params.row.balance) || ""}`,
      },
      {
        field: "id",
        headerName: "View",
        sortable: false,
        maxWidth: 60,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-datagrid",
        type: "actions",
        getActions: (params: GridValueGetterParams) => {
          if(params.row.status === "Void") return [];
          return [
            <GridActionsCellItem
              icon={<ArticleIcon color="primary" sx={{ fontSize: "20px" }} />}
              label=""
              onClick={() => {
                onViewStatement(params.row);
              }}
            />,
          ];
        },
      },
    ],
    []
  );

  return (
    <div className="data-table">
      <TableFilters
        onSearchChange={onSearchChange}
        search={search}
        status={status}
        clearSearch={clearSearch}
        setStatus={setStatus}
      />
      {data.length > 0 ? (
        <DataGrid
          checkboxSelection
          rows={data}
          getRowId={(row) => row.id}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSizeOptions={[10, 20, 30, 40, 50]}
          className="datagrid"
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
        />
      ) : (
        <Typography sx={{ padding: "20px" }}>
          No statement records found
        </Typography>
      )}
      {open && <StatementsDrawer setOpen={setOpen} />}
    </div>
  );
};

export default StatementTable;
